import {PublishingRecordForVersionHistoryPanel} from 'notebook/template-publishing/graphql/types';
import {PublishableVersionStatus} from 'notebook/template-publishing/TemplatePublishingConstants';
import {PublishableVersionInfo} from 'notebook/template-publishing/TemplatePublishingTypes';
import {Version} from 'notebook/version-history/VersionHistoryTypes';

export const shouldRenderRowAsPublishingItem = (version: Version): boolean =>
  version.publishableVersionInfo != null;

export const isPublishingVersion = (version: Version): boolean =>
  version.publishableVersionInfo != null &&
  version.publishableVersionInfo?.publishingStatus !== PublishableVersionStatus.DRAFT_NON_CURRENT;

export function getPublishableVersionInfoFromPublishingRecord(
  publishingRecord: PublishingRecordForVersionHistoryPanel | null,
  isLatestVersion: boolean
): PublishableVersionInfo {
  if (!publishingRecord) {
    return {
      publishingStatus: isLatestVersion
        ? PublishableVersionStatus.DRAFT
        : PublishableVersionStatus.DRAFT_NON_CURRENT,
    };
  }
  return {
    publishingStatus: publishingRecord.publishingStatus,
    versionName: publishingRecord.versionName,
    versionDescription: publishingRecord.versionDescription,
    publishableId: publishingRecord.publishableId,
    publishableVersionId: publishingRecord.publishableVersionId,
    publishedAt: publishingRecord.publishedAt,
    publishedByUser: publishingRecord.publishedByUser,
    unpublishedAt: publishingRecord.unpublishedAt,
    unpublishedByUser: publishingRecord.unpublishedByUser,
  };
}

/**
 * Transforms an array of versions and publishing records into an array of versions with publishable version info
 * @param versions array of versions assumed to be sorted by modifiedAt in descending order. This is also the order
 *                 that the versions will be displayed in the version history panel
 * @param publishingRecords array of publishingRecords
 * @returns array of versions with publishable version info on each version
 */
export function getVersionsWithPublishableVersionInfos(
  versions: Array<Version>,
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>
): Array<Version> {
  const publishableVersionIdToRecordMap = Object.fromEntries(
    publishingRecords.map((pr) => [pr.publishableVersionId, pr])
  );
  const versionsWithPublishableVersionInfo = versions.map((version, i) => {
    const publishingRecord = publishableVersionIdToRecordMap[version.apiIdentifier];
    const isLatestVersion = i === 0;
    const publishableVersionInfo = getPublishableVersionInfoFromPublishingRecord(
      publishingRecord ?? null,
      isLatestVersion
    );
    return {
      ...version,
      publishableVersionInfo,
    };
  });
  return versionsWithPublishableVersionInfo;
}

/**
 * Determines whether the open version is the latest draft version
 * @param versionsWithPublishing array of versions assumed to be sorted by modifiedAt in descending order.
 *                               This is also the order that the versions are displayed in the version history panel
 * @param openVersionId id of open version
 * @returns boolean indicating whether the open version is the latest draft version
 */
export function isOpenVersionLatestDraftVersion(
  versionsWithPublishing: Array<Version>,
  openVersionId: number
): boolean {
  const latestVersion = versionsWithPublishing[0];
  return (
    latestVersion.publishableVersionInfo?.publishingStatus === PublishableVersionStatus.DRAFT &&
    latestVersion.id === openVersionId
  );
}
